import { ListBase, useGetList, useListContext } from "ra-core";
import { Link, useSearchParams } from "react-router";

import { usePermission } from "../providers/usePermission";
import { DealCreateDialog } from "./DealCreateDialog";

import { formatCents } from "./format";
import { RECORD_LINK } from "./record/styles";
import { GraphError, GraphLoading } from "./GraphStatus";
import { SignalBadges } from "./SignalBadge";

interface GraphDeal {
  id: string;
  name: string;
  stage_id: string;
  pipeline_id: string;
  amount_cents: number | null;
  borrower_name?: string | null;
  signal_types?: string[];
}

interface Pipeline {
  id: string;
  name: string;
  sort_index: number;
}

interface PipelineStage {
  id: string;
  pipeline_id: string;
  label: string;
  sort_index: number;
}

export function GraphDealList() {
  return (
    <ListBase perPage={500}>
      <DealBoard />
    </ListBase>
  );
}

/**
 * One pipeline's board at a time. Every pipeline numbers its stages from the
 * same sort_index range, so mixing them interleaved unrelated columns.
 */
function DealBoard() {
  const { data: deals, isPending, error } = useListContext<GraphDeal>();
  const canCreate = usePermission("create_records");
  const pipelines = useGetList<Pipeline>("pipelines", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "sort_index", order: "ASC" },
  });
  const stages = useGetList<PipelineStage>("pipeline_stages", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "sort_index", order: "ASC" },
  });
  const [params, setParams] = useSearchParams();

  if (isPending || pipelines.isPending || stages.isPending) {
    return <GraphLoading shape="board" />;
  }
  const failure = error ?? pipelines.error ?? stages.error;
  if (failure) return <GraphError error={failure} noun="deal board" />;

  const allDeals = deals ?? [];
  const countFor = (pipelineId: string) =>
    allDeals.filter((deal) => deal.pipeline_id === pipelineId).length;
  const ordered = pipelines.data ?? [];
  // Default to the first pipeline that has deals, so the board is not empty.
  const selected =
    ordered.find((p) => p.id === params.get("pipeline")) ??
    ordered.find((p) => countFor(p.id) > 0) ??
    ordered[0];

  const columns = (stages.data ?? [])
    .filter((stage) => stage.pipeline_id === selected?.id)
    .sort((a, b) => a.sort_index - b.sort_index);
  const pipelineDeals = allDeals.filter(
    (deal) => deal.pipeline_id === selected?.id,
  );

  const totalOf = (list: GraphDeal[]) =>
    list.reduce((sum, deal) => sum + (Number(deal.amount_cents) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Deals</h1>
          <span className="text-sm text-muted-foreground">
            {pipelineDeals.length} in {selected?.name}
            {totalOf(pipelineDeals)
              ? ` · ${formatCents(totalOf(pipelineDeals))}`
              : ""}
          </span>
        </div>
        {canCreate ? <DealCreateDialog pipelineId={selected?.id} /> : null}
      </div>
      <nav
        aria-label="Pipelines"
        className="flex gap-1 overflow-x-auto rounded-lg border bg-card p-1 shadow-xs"
      >
        {ordered.map((pipeline) => {
          const isSelected = pipeline.id === selected?.id;
          return (
            <button
              key={pipeline.id}
              type="button"
              aria-current={isSelected ? "page" : undefined}
              className={
                "shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
                (isSelected
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground")
              }
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set("pipeline", pipeline.id);
                setParams(next);
              }}
            >
              {pipeline.name} ({countFor(pipeline.id)})
            </button>
          );
        })}
      </nav>
      {columns.length === 0 ? (
        <p className="text-muted-foreground">This pipeline has no stages.</p>
      ) : (
        <div className="flex items-start gap-3 overflow-x-auto pb-2">
          {columns.map((stage) => {
            const stageDeals = pipelineDeals.filter(
              (deal) => deal.stage_id === stage.id,
            );
            const total = totalOf(stageDeals);
            return (
              <section
                key={stage.id}
                aria-label={stage.label}
                className="w-72 shrink-0 rounded-xl border bg-muted/50 p-2"
              >
                <h2 className="flex items-center gap-2 px-2 pb-2 pt-1 text-sm font-semibold">
                  <span className="truncate">{stage.label}</span>
                  <span className="rounded-full bg-background px-1.5 text-xs font-medium text-muted-foreground">
                    {stageDeals.length}
                  </span>
                  {total ? (
                    <span className="ml-auto text-xs font-normal text-muted-foreground">
                      {formatCents(total)}
                    </span>
                  ) : null}
                </h2>
                {stageDeals.length === 0 ? (
                  <p className="px-2 pb-2 text-xs text-muted-foreground">
                    No deals
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {stageDeals.map((deal) => (
                      <li
                        key={deal.id}
                        className="rounded-lg border bg-card p-3 shadow-xs transition-shadow hover:shadow-sm"
                      >
                        <div className="flex items-start gap-2">
                          <Link
                            className={
                              RECORD_LINK + " min-w-0 flex-1 truncate text-sm"
                            }
                            to={`/deals/${deal.id}/show`}
                          >
                            {deal.name}
                          </Link>
                          <SignalBadges types={deal.signal_types} />
                        </div>
                        {deal.borrower_name || deal.amount_cents != null ? (
                          <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span className="truncate">
                              {deal.borrower_name}
                            </span>
                            {deal.amount_cents != null ? (
                              <span className="tabular-nums">
                                {formatCents(deal.amount_cents)}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
